import {
  UseMutationOptions,
  UseQueryOptions,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { capitalize } from 'lodash-es';

import { showSuccess } from '@components/toaster';
import { useThrottledMutation as useMutation } from '@hooks/use-throttled-mutation';
import { useScopedTranslate } from '@locale/translate';
import { downloadFile } from '@utils/browser';

import { useCurrentCompany } from 'api/authentication/hooks';
import { useWebSocket, waitForElasticRefresh } from 'api/hooks';
import { handleError, handlePossibleNonFieldError } from 'api/query-client';
import { BulkCreateTraveller } from 'api/traveller-journeys/types';
import { EmailError } from 'api/types';

import {
  BulkAddToJourneyType,
  BulkErrorResponse,
  CompanyTraveller,
  CompanyTravellersUserSettings,
  CreateCompanyTraveller,
  bulkAddCompanyTravellerToJourney,
  bulkAddCompanyTravellerToJourneyStatistics,
  bulkCreateCompanyTraveller,
  bulkDeleteCompanyTraveller,
  deleteCompanyTraveller,
  editCompanyTraveller,
  getBulkCreateTemplate,
  getCompanyTraveller,
  loadCompanyTravellerSettings,
  loadCompanyTravellers,
  loadCompanyTravellersFull,
  updateCompanyTravellerUserSettings,
} from './requests';

export const getCompanyTravellersQueryKey = (queryString?: string) =>
  ['company-travellers', queryString].filter(Boolean);

export const useCompanyTravellersPaginated = (
  queryString?: string,
  setCount?: (count: number) => void
) => {
  const company = useCurrentCompany();
  const query = useQuery({
    queryKey: getCompanyTravellersQueryKey(queryString),
    enabled: queryString !== '',
    queryFn: () => loadCompanyTravellers(queryString),
    onSuccess: (data) => {
      if (setCount) setCount(data.count);
    },
    keepPreviousData: true,
  });
  const queryClient = useQueryClient();
  useWebSocket(`company-travellers/${company?.shortId}/`, () => {
    queryClient.refetchQueries(getCompanyTravellersQueryKey(queryString));
  });
  return query;
};

export const useCompanyTravellers = (
  queryOptions: { staleTime?: number; keepPreviousData?: boolean } = {}
) => {
  return useQuery({
    queryKey: getCompanyTravellersQueryKey('full'),
    queryFn: loadCompanyTravellersFull,
    ...queryOptions,
  });
};

export const useCompanyTravellersFull = () => {
  const query = useCompanyTravellers();
  const company = useCurrentCompany();
  const queryClient = useQueryClient();
  useWebSocket(`company-travellers/${company?.shortId}/`, () => {
    queryClient.refetchQueries(getCompanyTravellersQueryKey('full'));
  });
  return query;
};

const defaultTravellerSettings = {
  viewState: {
    appliedFilters: [],
    conjunction: 'and',
    sortingState: [{ desc: false, id: 'name' }],
  },
} satisfies CompanyTravellersUserSettings['settings'];

export const useCompanyTravellersSettings = (
  options?: UseQueryOptions<CompanyTravellersUserSettings['settings']>
) => {
  return useQuery({
    queryKey: ['company-traveller-settings'],
    queryFn: loadCompanyTravellerSettings,
    select: (settings) => {
      return {
        viewState: settings?.viewState ?? defaultTravellerSettings.viewState,
      };
    },
    ...options,
  });
};

export const useUpdateCompanyTravellersSettings = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (settings: CompanyTravellersUserSettings) => {
      return updateCompanyTravellerUserSettings(settings);
    },
    onSuccess: (data) => {
      queryClient.setQueryData(['company-traveller-settings'], data.settings);
    },
  });
};

export const useCompanyTraveller = (shortId: string) => {
  return useQuery({
    queryKey: ['company-traveller', shortId],
    queryFn: () => getCompanyTraveller(shortId),
    enabled: !!shortId,
  });
};

export const useCreateCompanyTraveller = (
  onSuccess?: (traveller: CompanyTraveller) => void,
  options?: UseMutationOptions<unknown, EmailError, any>
) => {
  const { t } = useScopedTranslate(locale);

  const queryClient = useQueryClient();
  return useMutation({
    ...options,
    mutationFn: CreateCompanyTraveller,
    onSuccess: (data) => {
      waitForElasticRefresh(() => {
        queryClient.invalidateQueries(['company-traveller']);
        queryClient.refetchQueries(getCompanyTravellersQueryKey());
      });
      showSuccess(t('travellerCreated'));
      onSuccess?.(data);
    },
    onError: (data) => {
      const message = data.response?.data.email
        ? capitalize(data.response?.data.email[0].replace(/_/g, ' '))
        : t('travellerNotCreated');
      handleError(message);
    },
  });
};

export const useEditCompanyTraveller = (onSuccess?: () => void) => {
  const { t } = useScopedTranslate(locale);

  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ shortId, data }: { shortId: string; data: FormData }) =>
      editCompanyTraveller(shortId, data),
    onSuccess: async (...data) => {
      await waitForElasticRefresh(() => {
        queryClient.refetchQueries(['company-traveller', data[1].shortId]);
        queryClient.refetchQueries(getCompanyTravellersQueryKey());
        queryClient.invalidateQueries(['travellers']);
      });

      showSuccess(t('travellerEdited'));
      onSuccess?.();
    },
    onError: (data) => {
      handlePossibleNonFieldError(data, t('travellerNotEdited'));
    },
  });
};

export function useDeleteCompanyTraveller() {
  const { t } = useScopedTranslate(locale);
  const queryClient = useQueryClient();
  const queryKey = getCompanyTravellersQueryKey('full');

  return useMutation({
    onMutate: async (shortId) => {
      await queryClient.cancelQueries(queryKey);
      const previousData = queryClient.getQueryData<Array<CompanyTraveller>>(queryKey);

      queryClient.setQueryData<Array<CompanyTraveller>>(queryKey, (old) => {
        return old?.filter((traveller) => traveller.shortId !== shortId);
      });

      return { previousData };
    },
    mutationFn: (shortId: string) => deleteCompanyTraveller(shortId),
    onSuccess: () => {
      waitForElasticRefresh(() => {
        queryClient.invalidateQueries(getCompanyTravellersQueryKey());
        queryClient.invalidateQueries(['travellers']);
      });
      showSuccess(t('travellerDeleted'));
    },
    onError: (error, data, context) => {
      handleError(t('travellerNotDeleted'), () =>
        queryClient.setQueryData(queryKey, context?.previousData)
      );
    },
  });
}
export const useBulkCreateCompanyTravellerTemplate = () => {
  return useMutation({
    mutationFn: () => getBulkCreateTemplate(),
    onSuccess: (response) => {
      downloadFile(response, 'traveller-import-template.csv');
    },
  });
};

export const useBulkCreateCompanyTraveller = (
  setErrors: (errors: { travellers?: { [key: string]: string[] }[] }) => void,
  options?: UseMutationOptions<
    unknown,
    unknown,
    { travellers: BulkCreateTraveller[]; isSave: boolean; updateExisting?: boolean }
  >
) => {
  return useMutation({
    ...options,
    mutationFn: (data) => bulkCreateCompanyTraveller(data),
    onSuccess: (...data) => {
      options?.onSuccess?.(...data);
    },
    onError: (errors: BulkErrorResponse) => {
      handleError(() => {
        const errorData = errors.response?.data ?? {};
        if (errorData?.travellers) {
          setErrors(errorData);
        }
      });
    },
  });
};

export const useBulkDeleteCompanyTraveller = (
  options?: UseMutationOptions<unknown, unknown, { ids: string[]; selectAll: boolean }>
) => {
  const { t } = useScopedTranslate(locale);
  const queryClient = useQueryClient();
  const queryKey = getCompanyTravellersQueryKey('full');
  return useMutation({
    ...options,
    mutationFn: (data) => bulkDeleteCompanyTraveller(data),
    onMutate: async ({ ids }) => {
      await queryClient.cancelQueries(queryKey);
      const previousData = queryClient.getQueryData<Array<CompanyTraveller>>(queryKey);

      queryClient.setQueryData<Array<CompanyTraveller>>(queryKey, (old) => {
        return old?.filter((traveller) => !ids.includes(traveller.shortId));
      });

      return { previousData };
    },
    onSuccess: (...data) => {
      options?.onSuccess?.(...data);
      showSuccess(t('travellersDeleted'));
      waitForElasticRefresh(() => {
        queryClient.refetchQueries(getCompanyTravellersQueryKey());
      });
    },
    onError: (error, data, context) => {
      handleError(t('travellersNotDeleted'), () =>
        queryClient.setQueryData(queryKey, context?.previousData)
      );
    },
  });
};

export const useBulkAddCompanyTravellerToJourney = (
  options?: UseMutationOptions<{ started: number }, unknown, BulkAddToJourneyType>
) => {
  const { t } = useScopedTranslate(locale);
  const queryClient = useQueryClient();
  return useMutation({
    ...options,
    mutationFn: (data) => bulkAddCompanyTravellerToJourney(data),
    onError: () => {
      handleError(t('travellersNotAdded'));
    },
    onSuccess: (data, varibles, context) => {
      options?.onSuccess?.(data, varibles, context);
      waitForElasticRefresh(() => {
        queryClient.refetchQueries(getCompanyTravellersQueryKey());
        queryClient.invalidateQueries(['company-traveller']);
        queryClient.invalidateQueries(['traveller-journeys-status']);
      });
    },
  });
};

export const useBulkAddCompanyTravellerToJourneyStats = ({
  ids,
  journeyId,
}: {
  ids: string[];
  journeyId: string;
}) => {
  const query = `?ids=${ids.join(',')}&journeyId=${journeyId}`;
  return useQuery({
    queryKey: ['bulk-add-stats', journeyId, query],
    queryFn: () => bulkAddCompanyTravellerToJourneyStatistics(journeyId, ids),
  });
};

const locale = {
  en: {
    travellerCreated: 'Traveller created',
    travellerEdited: 'Traveller edited',
    travellerNotEdited: 'Traveller not edited',
    travellerNotCreated: 'Traveller not created',
    travellerDeleted: 'Traveller deleted',
    travellerNotDeleted: 'Traveller not deleted',
    travellersDeleted: 'Travellers deleted',
    travellersNotDeleted: 'Travellers could not be deleted',
    travellersNotAdded: 'Travellers could not be added to journey',
  },
};
